import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { useArticleCatalog } from "@/hooks/useArticleCatalog";
import { useSiteSettings } from "@/hooks/useSiteSettings";
import { formatCanonicalDateForDisplay, getSortableTimestampFromCanonicalDate } from "@/utils/date";
import { usePageSeo } from "@/hooks/usePageSeo";

const TYPE_COLORS: Record<string, string> = {
  Review: "bg-emerald-100 text-emerald-600",
  Compra: "bg-amber-100 text-amber-700",
};

export default function GuidesPage() {
  const [activeCategory, setActiveCategory] = useState("Todos");
  const [activeType, setActiveType] = useState("Todos");
  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<"recentes" | "populares">("populares");
  const { articles } = useArticleCatalog();
  const { settings } = useSiteSettings();

  const guideCategories = useMemo(
    () => ["Todos", ...Array.from(new Set(articles.map((article) => article.category)))],
    [articles]
  );

  const guideTypes = ["Todos", "Review", "Compra"];

  const featured = useMemo(() => articles.find((article) => article.featured) ?? articles[0], [articles]);

  usePageSeo({
    title: `Guias de Compra e Reviews | ${settings.siteName}`,
    description: settings.guidesPageDescription || settings.seoDescription,
    canonicalPath: "/guias",
    keywords: settings.seoKeywords,
    image: featured?.image || settings.logoUrl,
    siteName: settings.siteName,
    ogType: "website",
  });

  const trending = useMemo(
    () => articles.filter((article) => article.trending && article.id !== featured?.id).slice(0, 5),
    [articles, featured]
  );

  const filtered = useMemo(() => {
    let list = [...articles];

    if (activeCategory !== "Todos") list = list.filter((article) => article.category === activeCategory);
    if (activeType !== "Todos") list = list.filter((article) => article.type === activeType);

    if (search.trim()) {
      const query = search.toLowerCase();
      list = list.filter(
        (article) =>
          article.title.toLowerCase().includes(query) ||
          article.excerpt.toLowerCase().includes(query)
      );
    }

    if (sortBy === "populares") {
      list.sort((a, b) => b.views - a.views);
    } else {
      list.sort((a, b) => getSortableTimestampFromCanonicalDate(b.date) - getSortableTimestampFromCanonicalDate(a.date));
    }

    return list;
  }, [activeCategory, activeType, articles, search, sortBy]);

  const enableTrendingPromotion = !search.trim();

  const highlightedArticle = useMemo(() => {
    if (!enableTrendingPromotion) return null;
    return filtered.find((article) => article.trending) ?? null;
  }, [enableTrendingPromotion, filtered]);

  const sidebarTrending = useMemo(() => {
    if (!enableTrendingPromotion || !highlightedArticle) return [];
    return filtered
      .filter((article) => article.trending && article.id !== highlightedArticle.id)
      .slice(0, 5);
  }, [enableTrendingPromotion, filtered, highlightedArticle]);

  const promotedArticleIds = useMemo(
    () => new Set([highlightedArticle?.id, ...sidebarTrending.map((article) => article.id)].filter(Boolean)),
    [highlightedArticle, sidebarTrending]
  );

  const displayArticles = useMemo(
    () => filtered.filter((article) => !promotedArticleIds.has(article.id)),
    [filtered, promotedArticleIds]
  );

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <div className="pt-16 bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-5">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-4">
            <div>
              <div className="inline-flex items-center gap-2 bg-orange-100 text-orange-600 text-xs font-bold px-4 py-1.5 rounded-full mb-2">
                <i className="ri-book-open-line"></i>
                Guias & Reviews
              </div>
              <h1 className="text-2xl md:text-3xl font-black text-gray-900 mb-1.5">
                Guias de Compra e Reviews
              </h1>
              {settings.guidesPageDescription.trim() && (
                <p className="text-gray-500 text-sm max-w-xl">
                  {settings.guidesPageDescription}
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 md:px-6 pt-2 pb-8">
        <div className="bg-white rounded-2xl border border-gray-100 p-4 mb-6">
          <div className="flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
            <div className="flex flex-wrap gap-2">
              {guideCategories.map((category) => (
                <button
                  key={category}
                  onClick={() => setActiveCategory(category)}
                  className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                    activeCategory === category
                      ? "bg-orange-500 text-white"
                      : "bg-gray-100 text-gray-600 hover:bg-orange-50 hover:text-orange-500"
                  }`}
                >
                  {category}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 flex-shrink-0">
              <select
                value={activeType}
                onChange={(e) => setActiveType(e.target.value)}
                className="text-xs border border-gray-200 rounded-lg px-3 py-1.5 text-gray-600 focus:outline-none focus:border-orange-400 cursor-pointer bg-white"
              >
                {guideTypes.map((type) => (
                  <option key={type} value={type}>{type}</option>
                ))}
              </select>
              <div className="flex items-center gap-1 bg-gray-100 rounded-full p-1">
                {(["populares", "recentes"] as const).map((mode) => (
                  <button
                    key={mode}
                    onClick={() => setSortBy(mode)}
                    className={`px-3 py-1 rounded-full text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
                      sortBy === mode ? "bg-white text-gray-800" : "text-gray-500 hover:text-gray-700"
                    }`}
                  >
                    {mode === "populares" ? "Mais Vistos" : "Recentes"}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div className="flex items-center justify-between mb-5">
          <p className="text-gray-500 text-sm">
            <strong className="text-gray-800">{filtered.length}</strong> artigos encontrados
            {activeCategory !== "Todos" && <span> em <strong className="text-orange-500">{activeCategory}</strong></span>}
          </p>
          {(activeCategory !== "Todos" || activeType !== "Todos" || search) && (
            <button
              onClick={() => {
                setActiveCategory("Todos");
                setActiveType("Todos");
                setSearch("");
              }}
              className="text-xs text-gray-400 hover:text-orange-500 transition-colors cursor-pointer flex items-center gap-1"
            >
              <i className="ri-close-line"></i>
              Limpar filtros
            </button>
          )}
        </div>

        <div className="grid grid-cols-1 xl:grid-cols-[minmax(0,1fr)_300px] gap-6 items-start">
          <div>
            {highlightedArticle && (
              <Link
                to={`/artigo/${highlightedArticle.slug}`}
                className="group mb-5 block overflow-hidden rounded-2xl border border-gray-100 bg-white transition-all hover:-translate-y-0.5 hover:border-orange-200 cursor-pointer"
              >
                <div className="w-full h-72 md:h-[380px] overflow-hidden">
                  <img
                    src={highlightedArticle.image}
                    alt={highlightedArticle.title}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                  />
                </div>
                <div className="p-4 md:p-5">
                  <div className="flex items-center gap-2 mb-2 flex-wrap">
                    <span className="text-orange-500 text-xs font-bold">{highlightedArticle.category}</span>
                    <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_COLORS[highlightedArticle.type] ?? "bg-gray-100 text-gray-600"}`}>
                      {highlightedArticle.type}
                    </span>
                    <span className="ml-auto flex items-center gap-0.5 text-red-500 text-xs font-bold">
                      <i className="ri-fire-line"></i>
                      Trending
                    </span>
                  </div>
                  <h2 className="font-normal text-gray-800 group-hover:text-orange-500 transition-colors leading-snug mb-2 text-lg md:text-2xl">
                    {highlightedArticle.title}
                  </h2>
                  <p className="text-gray-500 text-sm leading-relaxed line-clamp-2 mb-3">{highlightedArticle.excerpt}</p>
                  <div className="flex flex-wrap items-center gap-3 text-gray-400 text-xs">
                    <span className="flex items-center gap-1"><i className="ri-time-line"></i>{highlightedArticle.readTime}</span>
                    <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{highlightedArticle.views.toLocaleString("pt-BR")}</span>
                    <span>{formatCanonicalDateForDisplay(highlightedArticle.date)}</span>
                  </div>
                </div>
              </Link>
            )}

            {displayArticles.length === 0 ? (
              highlightedArticle ? null : (
                <div className="text-center py-20 bg-white rounded-2xl border border-gray-100">
                  <div className="w-16 h-16 flex items-center justify-center mx-auto mb-4 bg-gray-100 rounded-full">
                    <i className="ri-search-line text-gray-300 text-2xl"></i>
                  </div>
                  <p className="text-gray-500 font-semibold">Nenhum artigo encontrado</p>
                  <p className="text-gray-400 text-sm mt-1">Tente outros filtros ou termos de busca</p>
                </div>
              )
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                {displayArticles.map((article) => (
                  <Link
                    key={article.id}
                    to={`/artigo/${article.slug}`}
                    className="group bg-white rounded-2xl border border-gray-100 hover:border-orange-200 overflow-hidden transition-all hover:-translate-y-0.5 cursor-pointer"
                  >
                    <div className="w-full overflow-hidden h-48">
                      <img
                        src={article.image}
                        alt={article.title}
                        className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-400"
                      />
                    </div>
                    <div className="p-4">
                      <div className="flex items-center gap-2 mb-2">
                        <span className="text-orange-500 text-xs font-bold">{article.category}</span>
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_COLORS[article.type] ?? "bg-gray-100 text-gray-600"}`}>
                          {article.type}
                        </span>
                        {article.trending && (
                          <span className="ml-auto flex items-center gap-0.5 text-red-500 text-xs font-bold">
                            <i className="ri-fire-line"></i>
                            Trending
                          </span>
                        )}
                      </div>
                      <h3 className="font-normal text-gray-800 group-hover:text-orange-500 transition-colors leading-snug mb-2 text-sm">
                        {article.title}
                      </h3>
                      <p className="text-gray-500 text-xs leading-relaxed line-clamp-2 mb-3">{article.excerpt}</p>
                      <div className="flex flex-wrap items-center gap-3 text-gray-400 text-xs">
                        <span className="flex items-center gap-1"><i className="ri-time-line"></i>{article.readTime}</span>
                        <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{article.views.toLocaleString("pt-BR")}</span>
                        <span>{formatCanonicalDateForDisplay(article.date)}</span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
            )}
          </div>

          {sidebarTrending.length > 0 && (
            <aside className="xl:sticky xl:top-24 space-y-4">
              <div className="flex items-center gap-2 px-1">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-fire-line text-orange-500"></i>
                </div>
                <span className="font-black text-gray-800 text-sm">Em Alta Agora</span>
              </div>

              {sidebarTrending.map((article) => (
                <Link
                  key={article.id}
                  to={`/artigo/${article.slug}`}
                  className="group flex items-center gap-3 rounded-2xl border border-gray-200 bg-white p-3 transition-all hover:border-orange-200 hover:bg-orange-50/30 cursor-pointer"
                >
                  <div className="h-[72px] w-[72px] overflow-hidden rounded-xl bg-gray-100 flex-shrink-0">
                    <img
                      src={article.image}
                      alt={article.title}
                      className="h-full w-full object-cover object-top group-hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="mb-1.5 flex items-center gap-1.5 flex-wrap">
                      <span className="text-orange-500 text-xs font-bold">{article.category}</span>
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${TYPE_COLORS[article.type] ?? "bg-gray-100 text-gray-600"}`}>
                        {article.type}
                      </span>
                    </div>
                    <h3 className="text-gray-900 text-sm font-normal leading-snug line-clamp-2 group-hover:text-orange-500 transition-colors mb-2">
                      {article.title}
                    </h3>
                    <div className="flex items-center gap-3 text-gray-400 text-xs">
                      <span className="flex items-center gap-1"><i className="ri-time-line"></i>{article.readTime}</span>
                      <span className="flex items-center gap-1"><i className="ri-eye-line"></i>{article.views.toLocaleString("pt-BR")}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </aside>
          )}
        </div>
      </main>

      <Footer />
    </div>
  );
}